import apiClient from '../../shared/services/apiClient';

/**
 * Payment Service
 * Handles Stripe and PayPal payment operations
 */
export const paymentService = {
    // ==========================================
    // Payment Methods Management
    // ==========================================

    /**
     * Get supported payment methods
     * @returns {Promise} Payment methods list
     */
    getPaymentMethods: async () => {
        try {
            const response = await apiClient.get('/api/user/payment/methods');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get saved payment methods
     * @returns {Promise} Saved payment methods
     */
    getSavedMethods: async () => {
        try {
            const response = await apiClient.get('/api/user/payment/saved-methods');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Set default payment method
     * @param {number} paymentMethodId - Payment method ID
     * @returns {Promise} Result
     */
    setDefaultMethod: async (paymentMethodId) => {
        try {
            const response = await apiClient.post('/api/user/payment/set-default', {
                payment_method_id: paymentMethodId
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Delete payment method
     * @param {number} paymentMethodId - Payment method ID
     * @returns {Promise} Result
     */
    deleteMethod: async (paymentMethodId) => {
        try {
            const response = await apiClient.post('/api/user/payment/delete-method', {
                payment_method_id: paymentMethodId
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // Stripe Payment
    // ==========================================

    /**
     * Get Stripe configuration
     * @returns {Promise} Stripe config
     */
    getStripeConfig: async () => {
        try {
            const response = await apiClient.get('/api/user/payment/stripe/config');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Create Stripe payment intent
     * @param {number} invoiceId - Invoice ID
     * @param {string} paymentMethodType - Payment method type (card, alipay, wechat_pay)
     * @returns {Promise} Payment intent data
     */
    createStripeIntent: async (invoiceId, { replacePending = false } = {}) => {
        try {
            // The page shows one friendly message for these (incl. PENDING_BANK_TRANSFER / PAYMENT_PROCESSING)
            const response = await apiClient.post('/api/user/payment/stripe/create-intent', {
                invoice_id: invoiceId,
                replace_pending: replacePending ? 1 : 0,
            }, { skipGlobalErrorToast: true });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Charge with saved card
     * @param {number} invoiceId - Invoice ID
     * @param {number} paymentMethodId - Saved payment method ID
     * @returns {Promise} Payment result
     */
    chargeWithSavedCard: async (invoiceId, paymentMethodId) => {
        try {
            const response = await apiClient.post('/api/user/payment/stripe/charge-saved', {
                invoice_id: invoiceId,
                payment_method_id: paymentMethodId
            }, { skipGlobalErrorToast: true });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Check Stripe payment status
     * @param {string} paymentIntentId - Payment intent ID
     * @returns {Promise} Payment status
     */
    checkStripeStatus: async (paymentIntentId) => {
        try {
            const response = await apiClient.get('/api/user/payment/stripe/status', {
                params: { payment_intent_id: paymentIntentId },
                skipGlobalErrorToast: true,
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * In-flight / awaiting-transfer Stripe payment for an invoice (ACH processing, bank transfer instructions)
     * @param {number} invoiceId
     */
    getStripePending: async (invoiceId) => {
        try {
            const response = await apiClient.get('/api/user/payment/stripe/pending', { params: { invoice_id: invoiceId }, skipGlobalErrorToast: true });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Create setup intent for adding payment method
     * @returns {Promise} Setup intent data
     */
    createSetupIntent: async () => {
        try {
            const response = await apiClient.post('/api/user/payment/stripe/setup-intent');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Save payment method after setup
     * @param {string} paymentMethodId - Stripe payment method ID
     * @returns {Promise} Result
     */
    savePaymentMethod: async ({ setupIntentId, paymentMethodId } = {}) => {
        try {
            const response = await apiClient.post('/api/user/payment/stripe/save-method', {
                setup_intent_id: setupIntentId,
                payment_method_id: paymentMethodId,
            }, { skipGlobalErrorToast: true });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // PayPal Payment
    // ==========================================

    /**
     * Get PayPal configuration
     * @returns {Promise} PayPal config
     */
    getPayPalConfig: async () => {
        try {
            const response = await apiClient.get('/api/user/payment/paypal/config');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Create PayPal order
     * @param {number} invoiceId - Invoice ID
     * @returns {Promise} PayPal order data
     */
    createPayPalOrder: async (invoiceId) => {
        try {
            const response = await apiClient.post('/api/user/payment/paypal/create-order', {
                invoice_id: invoiceId
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Capture PayPal order
     * @param {string} orderId - PayPal order ID
     * @returns {Promise} Capture result
     */
    capturePayPalOrder: async (orderId) => {
        try {
            const response = await apiClient.post('/api/user/payment/paypal/capture-order', {
                order_id: orderId
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Check PayPal order status
     * @param {string} orderId - PayPal order ID
     * @returns {Promise} Order status
     */
    checkPayPalStatus: async (orderId) => {
        try {
            const response = await apiClient.get('/api/user/payment/paypal/status', {
                params: { order_id: orderId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },
};

export default paymentService;
